import { useCallback, useEffect, useState } from 'react'
import { fetchSpeaker, type SpeakerKind } from './api'
import { SPEAKER_POLL_MS } from './constants'

/** Someone other than this dispatcher holding a camera's speaker. */
export interface HeldBy {
  name: string
  kind: SpeakerKind
}

/**
 * Polls who holds the speaker of the camera live view is showing, so the
 * controls can say so before anyone tries. Polled rather than pushed, like
 * camera health: one answer feeds both live view and the speak panel.
 */
export function useSpeaker(target: { siteId: string; camera: string } | null) {
  // Kept with the camera it answers for, so a switch never shows the previous
  // camera's speaker while the next one's answer is in flight.
  const [answer, setAnswer] = useState<{ key: string; heldBy: HeldBy | null; speech: boolean } | null>(null)
  const siteId = target?.siteId
  const camera = target?.camera
  const key = siteId && camera ? `${siteId}\u0000${camera}` : null
  // Bumped to ask again at once — after a refusal, or when this dispatcher
  // lets go — rather than up to a poll interval later.
  const [asks, setAsks] = useState(0)
  const refresh = useCallback(() => setAsks((n) => n + 1), [])

  useEffect(() => {
    if (!siteId || !camera) return
    const asked = `${siteId}\u0000${camera}`
    let cancelled = false
    const poll = () => {
      fetchSpeaker(siteId, camera).then((s) => {
        if (cancelled) return
        setAnswer({ key: asked, heldBy: s.speaking && !s.yours ? { name: s.speaker, kind: s.kind } : null, speech: s.speech !== false })
      }).catch(() => {}) // keep the last answer; the server still refuses a second speaker
    }
    poll()
    const t = setInterval(poll, SPEAKER_POLL_MS)
    return () => { cancelled = true; clearInterval(t) }
  }, [siteId, camera, asks])

  const current = answer && answer.key === key ? answer : null
  // Until the server has answered, speech counts as available: hiding the
  // speak icon for two seconds on every camera would be worse than showing
  // it on a server where it fails.
  return { heldBy: current?.heldBy ?? null, speechAvailable: current?.speech ?? true, refresh }
}

export function describeHolder(heldBy: HeldBy): string {
  return heldBy.kind === 'microphone'
    ? `${heldBy.name} is talking live on this camera`
    : `${heldBy.name} is playing a message on this camera`
}
